import { c as _c } from "react/compiler-runtime";
import { addDays, differenceInHours, formatDistanceToNow } from 'date-fns';
import { useContext } from 'react';
import { BackpressureIssueWithData, DeliveryIssueWithData, Issue, RequestIssueWithData, TransformationIssueWithData } from '../../../../../../../typings/Issue.interface';
import { APIWebhook } from '../../../../../../../typings/Webhook.interface';
import { pluralize } from '../../../../utils';
import { RelativeDate } from '../../../../utils/date';
import { getIssueTitleParts, ISSUE_STATUS_CONFIGS, issue_type_configs } from '../../../../utils/issues';
import { getMsIntervalByUnit } from '../../../../utils/rules';
import { getRejectionCauseBreakdown } from '../../../../utils/rejection-causes';
import Badge from '../../../common/base/Badge';
import Chip from '../../../common/base/Chip';
import Container from '../../../common/base/Container';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import ChartInteractionGroup from '../../../common/Chart/ChartInteractionGroup';
import { Div } from '../../../common/helpers/StyledUtils';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, VIEW_NAV_HEADER_HEIGHT, ViewMetadataCopyable, ViewMetadataItem, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import BackpressureIssue, { useBackpressureChartsDefinition } from './BackpressureIssue';
import DeliveryIssue from './DeliveryIssue';
import RequestIssue from './RequestIssue';
import { RequestAlerts } from '../Request';
import TransformationIssue from './TransformationIssue';
interface IssueOverviewProps {
  issue: Issue;
  start_date: Date;
  end_date: Date;
  relative_date?: RelativeDate;
  setDate: (date: any) => void;
  refresh_key: string;
  render_data: {
    webhooks_by_id: any;
    transformations_by_id: any;
    destinations_by_id: any;
    sources_by_id: any;
  };
  mutateIssue: () => void;
}
const IssueSidebarTopSection = t0 => {
  const $ = _c(62);
  const {
    issue,
    render_data,
    description
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== description || $[1] !== issue.aggregation_keys || $[2] !== issue.id || $[3] !== issue.reference || $[4] !== issue.status || $[5] !== issue.type || $[6] !== render_data) {
    const impacted_connections = (issue.type === "delivery" && issue.aggregation_keys?.webhook_id?.map(webhook_id => render_data.webhooks_by_id?.[webhook_id] || webhook_id)) as (APIWebhook | string)[] || [];
    let t8;
    if ($[15] !== issue.reference || $[16] !== issue.type) {
      t8 = issue.type === "backpressure" ? issue.reference.triggered_by || {
        type: "destination" as const
      } : null;
      $[15] = issue.reference;
      $[16] = issue.type;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    const backpressure_triggered_by = t8;
    T0 = ViewSplitPaneSidebarSection;
    if ($[18] !== description) {
      t1 = description && <ViewMetadataItem label="Description"><Text dark={true}>{description}</Text></ViewMetadataItem>;
      $[18] = description;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    const t9 = ISSUE_STATUS_CONFIGS[issue.status].badge_theme !== "muted";
    const t10 = ISSUE_STATUS_CONFIGS[issue.status];
    let t11;
    if ($[20] !== t10.badge_theme) {
      t11 = {
        [t10.badge_theme]: true
      };
      $[20] = t10.badge_theme;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    const t12 = ISSUE_STATUS_CONFIGS[issue.status];
    const t13 = ISSUE_STATUS_CONFIGS[issue.status];
    if ($[22] !== t11 || $[23] !== t12.icon || $[24] !== t13.label || $[25] !== t9) {
      t2 = <ViewMetadataItem label="Status"><Badge subtle={t9} {...t11} icon={t12.icon}>{t13.label}</Badge></ViewMetadataItem>;
      $[22] = t11;
      $[23] = t12.icon;
      $[24] = t13.label;
      $[25] = t9;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    const t14 = issue_type_configs[issue.type];
    const t15 = issue_type_configs[issue.type];
    if ($[27] !== t14.icon || $[28] !== t15.label) {
      t3 = <ViewMetadataItem label="Issue type"><Badge neutral={true} icon={t14.icon}>{t15.label}</Badge></ViewMetadataItem>;
      $[27] = t14.icon;
      $[28] = t15.label;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    if ($[30] !== issue.aggregation_keys || $[31] !== issue.type || $[32] !== render_data) {
      t4 = issue.type === "transformation" && <ViewMetadataItem label="Transformation"><Div style={{
          overflow: "hidden"
        }}><Chip mono={true} outline={true} label_icon="transformation" label={render_data.transformations_by_id?.[issue.aggregation_keys.transformation_id[0]]?.name} to={`/transformations/${issue.aggregation_keys.transformation_id[0]}`} /></Div></ViewMetadataItem>;
      $[30] = issue.aggregation_keys;
      $[31] = issue.type;
      $[32] = render_data;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    if ($[34] !== backpressure_triggered_by?.delivery_group || $[35] !== backpressure_triggered_by?.type || $[36] !== issue.aggregation_keys || $[37] !== issue.id || $[38] !== issue.type || $[39] !== render_data) {
      t5 = issue.type === "backpressure" && <><ViewMetadataItem label="Destination"><Div style={{
            overflow: "hidden"
          }}><Chip mono={true} outline={true} label_icon="destination" label={render_data.destinations_by_id?.[issue.aggregation_keys.destination_id[0]]?.name} to={`/destinations/${issue.aggregation_keys.destination_id[0]}`} /></Div></ViewMetadataItem><ViewMetadataItem label="Triggered by"><Div style={{
            overflow: "hidden"
          }}><Chip mono={true} outline={true} label_icon={backpressure_triggered_by?.type === "destination" ? "destination" : "rate_limit"} label={backpressure_triggered_by?.type === "destination" ? "Destination" : `${backpressure_triggered_by?.delivery_group || "Unknown"} (delivery group)`} to={backpressure_triggered_by?.type === "destination" ? `/destinations/${issue.aggregation_keys.destination_id[0]}` : `/issues/${issue.id}/delivery-groups`} /></Div></ViewMetadataItem></>;
      $[34] = backpressure_triggered_by?.delivery_group;
      $[35] = backpressure_triggered_by?.type;
      $[36] = issue.aggregation_keys;
      $[37] = issue.id;
      $[38] = issue.type;
      $[39] = render_data;
      $[40] = t5;
    } else {
      t5 = $[40];
    }
    if ($[41] !== issue.aggregation_keys || $[42] !== issue.type || $[43] !== render_data) {
      t6 = issue.type === "request" && <ViewMetadataItem label="Source"><Div style={{
          overflow: "hidden"
        }}><Chip mono={true} outline={true} label_icon="source" label={render_data.sources_by_id?.[issue.aggregation_keys.source_id[0]]?.name} to={`/sources/${issue.aggregation_keys.source_id[0]}`} /></Div></ViewMetadataItem>;
      $[41] = issue.aggregation_keys;
      $[42] = issue.type;
      $[43] = render_data;
      $[44] = t6;
    } else {
      t6 = $[44];
    }
    t7 = issue.type === "delivery" && impacted_connections.length > 0 && <ViewMetadataItem label={impacted_connections.length > 1 ? "Connections" : "Connection"}><Div flex={{
        direction: "column",
        gap: 2
      }} style={{
        overflow: "hidden"
      }}>{impacted_connections.map(_temp)}</Div></ViewMetadataItem>;
    $[0] = description;
    $[1] = issue.aggregation_keys;
    $[2] = issue.id;
    $[3] = issue.reference;
    $[4] = issue.status;
    $[5] = issue.type;
    $[6] = render_data;
    $[7] = T0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    T0 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
  }
  const t8 = issue.type === "backpressure" ? "Issue trigger" : "Triggered by";
  let t9;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      overflow: "hidden"
    };
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  const t10 = `/issue-triggers/${issue.issue_trigger_id}`;
  let t11;
  if ($[46] !== issue.issue_trigger_id || $[47] !== t10) {
    t11 = <Div style={t9}><Chip outline={true} mono={true} label_icon="issue_trigger" label={issue.issue_trigger_id} to={t10} /></Div>;
    $[46] = issue.issue_trigger_id;
    $[47] = t10;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] !== t11 || $[50] !== t8) {
    t12 = <ViewMetadataItem label={t8}>{t11}</ViewMetadataItem>;
    $[49] = t11;
    $[50] = t8;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== T0 || $[53] !== t1 || $[54] !== t12 || $[55] !== t2 || $[56] !== t3 || $[57] !== t4 || $[58] !== t5 || $[59] !== t6 || $[60] !== t7) {
    t13 = <T0>{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t12}</T0>;
    $[52] = T0;
    $[53] = t1;
    $[54] = t12;
    $[55] = t2;
    $[56] = t3;
    $[57] = t4;
    $[58] = t5;
    $[59] = t6;
    $[60] = t7;
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  return t13;
};
const IssueSidebarMetadata = t0 => {
  const $ = _c(21);
  const {
    issue
  } = t0;
  const t1 = issue.type === "request" ? "Issue metadata" : "Metadata";
  let t2;
  if ($[0] !== issue.id) {
    t2 = <ViewMetadataItem label="Issue ID"><ViewMetadataCopyable value={issue.id} /></ViewMetadataItem>;
    $[0] = issue.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = issue.first_seen_at;
  let t4;
  if ($[2] !== issue.first_seen_at) {
    t4 = formatDistanceToNow(new Date(issue.first_seen_at));
    $[2] = issue.first_seen_at;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <Text>{t4} old</Text>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== issue.first_seen_at || $[7] !== t5) {
    t6 = <ViewMetadataItem label="First seen"><Tooltip tooltip={t3} copyable={true} side="bottom">{t5}</Tooltip></ViewMetadataItem>;
    $[6] = issue.first_seen_at;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = issue.last_seen_at;
  let t8;
  if ($[9] !== issue.last_seen_at) {
    t8 = formatDistanceToNow(new Date(issue.last_seen_at), {
      addSuffix: true
    });
    $[9] = issue.last_seen_at;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <Text>{t8}</Text>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== issue.last_seen_at || $[14] !== t9) {
    t10 = <ViewMetadataItem label="Last seen"><Tooltip tooltip={t7} copyable={true} side="bottom">{t9}</Tooltip></ViewMetadataItem>;
    $[13] = issue.last_seen_at;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t1 || $[17] !== t10 || $[18] !== t2 || $[19] !== t6) {
    t11 = <ViewSplitPaneSidebarSection title={t1} bordered={true}>{t2}{t6}{t10}</ViewSplitPaneSidebarSection>;
    $[16] = t1;
    $[17] = t10;
    $[18] = t2;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
};
const issue_description: Partial<Record<string, string>> = {
  transformation: 'Update your transformation code and retry the impacted ignored events to fix this issue.',
  request: 'This source is rejecting incoming webhook requests. Review the rejection cause and update your source configuration.'
};
const getIssueDescription = (issue: Issue): string | undefined => {
  if (issue.type !== 'backpressure') {
    return issue_description[issue.type];
  }

  // The delay comes from the issue's trigger, formatted like the issue title.
  const [value, unit] = getMsIntervalByUnit(issue.aggregation_keys.delay[0], 'delay');
  return `This destination has a backlog of attempts that will take more than ${value} ${pluralize(value, unit)} to clear.`;
};
export const IssueOverview = t0 => {
  const $ = _c(72);
  const {
    issue,
    start_date,
    end_date,
    relative_date,
    setDate,
    refresh_key,
    render_data,
    mutateIssue
  } = t0;
  const {
    query
  } = useSearchQuery();
  const {
    subscription,
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const {
    charts: backpressure_charts,
    onDateRangeChanged: onBackpressureDateRangeChanged
  } = useBackpressureChartsDefinition((issue as BackpressureIssueWithData).data.destination, Number(refresh_key));
  const expanded_chart = query.expanded && backpressure_charts[query.expanded];
  let t1;
  if ($[0] !== issue || $[1] !== render_data) {
    const title_parts = getIssueTitleParts(issue, render_data, true);
    t1 = title_parts.map(_temp2).join(" ");
    $[0] = issue;
    $[1] = render_data;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const issue_title = t1;
  let t2;
  if ($[3] !== issue) {
    t2 = getIssueDescription(issue);
    $[3] = issue;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const description = t2;
  const request_issue = issue.type === "request" ? issue as RequestIssueWithData : null;
  const request = request_issue?.data?.request;
  const request_source_id = request_issue?.aggregation_keys.source_id[0];
  const request_source = request_source_id && render_data.sources_by_id?.[request_source_id];
  let t3;
  if ($[5] !== request || $[6] !== request_source) {
    t3 = request && request_source && request.rejection_cause && getRejectionCauseBreakdown(request.rejection_cause)({
      request,
      source: request_source
    });
    $[5] = request;
    $[6] = request_source;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const request_rejection_cause_breakdown = t3;
  let t4;
  if ($[8] !== request || $[9] !== subscription) {
    t4 = request && subscription ? differenceInHours(addDays(new Date(request.ingested_at), subscription.retention_days), new Date()) : null;
    $[8] = request;
    $[9] = subscription;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const request_hours_before_archive = t4;
  if (expanded_chart) {
    let t5;
    if ($[11] !== onBackpressureDateRangeChanged) {
      t5 = range => onBackpressureDateRangeChanged({
        relative: undefined,
        min: range.min,
        max: range.max
      });
      $[11] = onBackpressureDateRangeChanged;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        t: 6
      };
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== expanded_chart) {
      t7 = <Container left={true} p={t6}>{expanded_chart}</Container>;
      $[14] = expanded_chart;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t5 || $[17] !== t7) {
      t8 = <ChartInteractionGroup onTimeRangeSelected={t5}>{t7}</ChartInteractionGroup>;
      $[16] = t5;
      $[17] = t7;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    return t8;
  }
  const has_secondary_nav = issue.type === "transformation" || issue.type === "backpressure" && (issue.reference.delivery_groups?.length || 0) > 0;
  const t5 = has_secondary_nav ? undefined : VIEW_NAV_HEADER_HEIGHT;
  let t6;
  if ($[19] !== issue_title) {
    t6 = <ViewSplitPaneSidebarHeader title={issue_title} />;
    $[19] = issue_title;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== request_hours_before_archive || $[22] !== request_rejection_cause_breakdown) {
    t7 = request_hours_before_archive !== null && <RequestAlerts rejection_cause_breakdown={request_rejection_cause_breakdown || null} hours_before_archive={request_hours_before_archive} />;
    $[21] = request_hours_before_archive;
    $[22] = request_rejection_cause_breakdown;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== description || $[25] !== issue || $[26] !== render_data) {
    t8 = <IssueSidebarTopSection issue={issue} render_data={render_data} description={description} />;
    $[24] = description;
    $[25] = issue;
    $[26] = render_data;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== issue) {
    t9 = <IssueSidebarMetadata issue={issue} />;
    $[28] = issue;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t10 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t6}{t7}{t8}{t9}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== end_date || $[36] !== issue || $[37] !== refresh_key || $[38] !== relative_date || $[39] !== render_data || $[40] !== setDate || $[41] !== start_date) {
    t11 = issue.type === "delivery" && <DeliveryIssue render_data={render_data} issue={issue as DeliveryIssueWithData} start_date={start_date} end_date={end_date} relative_date={relative_date} setDate={setDate} refresh_key={refresh_key} />;
    $[35] = end_date;
    $[36] = issue;
    $[37] = refresh_key;
    $[38] = relative_date;
    $[39] = render_data;
    $[40] = setDate;
    $[41] = start_date;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== end_date || $[44] !== issue || $[45] !== refresh_key || $[46] !== relative_date || $[47] !== setDate || $[48] !== start_date) {
    t12 = issue.type === "transformation" && <TransformationIssue issue={issue as TransformationIssueWithData} start_date={start_date} end_date={end_date} relative_date={relative_date} setDate={setDate} refresh_key={refresh_key} />;
    $[43] = end_date;
    $[44] = issue;
    $[45] = refresh_key;
    $[46] = relative_date;
    $[47] = setDate;
    $[48] = start_date;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== issue || $[51] !== mutateIssue || $[52] !== refresh_key) {
    t13 = issue.type === "backpressure" && <BackpressureIssue key={refresh_key} issue={issue as BackpressureIssueWithData} mutateIssue={mutateIssue} />;
    $[50] = issue;
    $[51] = mutateIssue;
    $[52] = refresh_key;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== end_date || $[55] !== issue || $[56] !== refresh_key || $[57] !== relative_date || $[58] !== render_data || $[59] !== setDate || $[60] !== start_date) {
    t14 = issue.type === "request" && <RequestIssue render_data={render_data} issue={issue as RequestIssueWithData} start_date={start_date} end_date={end_date} relative_date={relative_date} setDate={setDate} refresh_key={refresh_key} />;
    $[54] = end_date;
    $[55] = issue;
    $[56] = refresh_key;
    $[57] = relative_date;
    $[58] = render_data;
    $[59] = setDate;
    $[60] = start_date;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] !== t11 || $[63] !== t12 || $[64] !== t13 || $[65] !== t14) {
    t15 = <StyledViewSplitPaneContent>{t11}{t12}{t13}{t14}</StyledViewSplitPaneContent>;
    $[62] = t11;
    $[63] = t12;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== is_degraded_banner_active || $[68] !== t10 || $[69] !== t15 || $[70] !== t5) {
    t16 = <StyledViewSplitPaneLayout banner_active={is_degraded_banner_active} nav_height={t5}>{t10}{t15}</StyledViewSplitPaneLayout>;
    $[67] = is_degraded_banner_active;
    $[68] = t10;
    $[69] = t15;
    $[70] = t5;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  return t16;
};
function _temp(connection) {
  return <Chip key={typeof connection === "string" ? connection : connection?.id} outline={true} mono={true} label={typeof connection === "string" ? connection : connection?.full_name} to={`/connections/${typeof connection === "string" ? connection : connection?.id}`} />;
}
function _temp2(part) {
  return part.label;
}